import { useState, useEffect, useCallback } from "react";
import { Select } from "../../common";
import { graphBuilderClient } from "../../../services/api";

type Preset = {
  id: string;
  name: string;
  description: string;
  threshold: number;
  rateCodingScheme: string;
  fusionMode: string;
  plasticity: string;
};

type NeuralPresetSelectorProps = {
  nodeType: string;
  currentThreshold?: number;
  currentFusionMode?: string;
  onPresetApply: (preset: {
    threshold: number;
    rateCodingScheme: string;
    fusionMode: string;
    plasticity: string;
    presetId: string;
  }) => void;
};

const NeuralPresetSelector = ({
  nodeType,
  currentThreshold,
  currentFusionMode,
  onPresetApply,
}: NeuralPresetSelectorProps) => {
  const [presets, setPresets] = useState<Preset[]>([]);
  const [selectedPresetId, setSelectedPresetId] = useState<string>("_custom");
  const [threshold, setThreshold] = useState<number>(currentThreshold ?? 0.5);
  const [loading, setLoading] = useState(false);

  const fetchPresets = useCallback(async () => {
    if (!nodeType) return;
    setLoading(true);
    try {
      const res = await graphBuilderClient.get(`/api/presets/node?node_type=${encodeURIComponent(nodeType)}`);
      const data = res?.data || res;
      setPresets(data.presets || []);
      if (data.presets?.length > 0 && selectedPresetId === "_custom") {
        const match = data.presets.find(
          (p: Preset) => Math.abs(p.threshold - (currentThreshold ?? 0.5)) < 0.05 && p.fusionMode === currentFusionMode
        );
        if (match) setSelectedPresetId(match.id);
      }
    } catch {
      setPresets([]);
    } finally {
      setLoading(false);
    }
  }, [nodeType]);

  useEffect(() => {
    fetchPresets();
  }, [fetchPresets]);

  useEffect(() => {
    setThreshold(currentThreshold ?? 0.5);
  }, [currentThreshold]);

  const handlePresetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const presetId = e.target.value;
    setSelectedPresetId(presetId);
    if (presetId === "_custom") return;
    const preset = presets.find((p) => p.id === presetId);
    if (preset) {
      setThreshold(preset.threshold);
      onPresetApply({
        threshold: preset.threshold,
        rateCodingScheme: preset.rateCodingScheme,
        fusionMode: preset.fusionMode,
        plasticity: preset.plasticity,
        presetId: preset.id,
      });
    }
  };

  const handleThresholdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    setThreshold(val);
    setSelectedPresetId("_custom");
    onPresetApply({
      threshold: val,
      rateCodingScheme: "frequency",
      fusionMode: currentFusionMode || "weighted_avg",
      plasticity: "none",
      presetId: "_custom",
    });
  };

  if (!nodeType || loading) return null;
  if (presets.length === 0) return null;

  const presetOptions = [
    { id: "_custom", value: "_custom", label: "Custom" },
    ...presets.map((p) => ({
      id: p.id,
      value: p.id,
      label: `${p.name} — ${p.description}`,
    })),
  ];

  return (
    <div className="neural-preset-selector">
      <div className="neural-preset-selector__header">
        <p className="node-ratecoding-section__eyebrow">Neural Behavior</p>
        <p className="node-ratecoding-section__description">
          Select a behavior preset or fine-tune with the slider below.
        </p>
      </div>
      <div className="synapse-entity-modal__form-group">
        <Select
          id="neural-preset"
          name="neuralPreset"
          value={selectedPresetId}
          onChange={handlePresetChange}
          variant="modal"
          options={presetOptions}
          label="Behavior Preset"
        />
      </div>
      <div className="synapse-entity-modal__form-group neural-preset-selector__slider">
        <label htmlFor="threshold-slider" className="neural-preset-selector__label">
          Activation Sensitivity: <strong>{threshold.toFixed(2)}</strong>
          <span className="neural-preset-selector__hint">
            {threshold < 0.3 ? " (broad)" : threshold < 0.6 ? " (balanced)" : threshold < 0.8 ? " (selective)" : " (strict)"}
          </span>
        </label>
        <input
          id="threshold-slider"
          type="range"
          min="0.05"
          max="0.95"
          step="0.05"
          value={threshold}
          onChange={handleThresholdChange}
          className="neural-preset-selector__range"
        />
        <div className="neural-preset-selector__range-labels">
          <span>Broad</span>
          <span>Strict</span>
        </div>
      </div>
    </div>
  );
};

export default NeuralPresetSelector;
